// src/components/profile/ProfileSectionForm.jsx
import React, { useState, useEffect } from 'react';
import { X, Save, Star, Plus, Trash2, File, MessageCircle, Image as ImageIcon, Upload, 
  Link, Calendar, Camera, Edit3, User, Settings 
} from 'lucide-react';
import { FileUpload } from '../common/FileUpload';
import { profileService } from '../../services/profileService';
import DemoFormFields from './forms/DemoFormFields';
import HeroFormFields from './forms/HeroFormFields';

export const ProfileSectionForm = ({ section, profileType, bookId, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    type: 'about',
    title: '',
    status: 'active',
    order: 0,
    ...section
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (section) {
      setFormData(section);
    }
  }, [section]);

  const handleSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving section:', error);
    } finally {
      setSaving(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title?.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!formData.type) {
      errors.type = 'Section type is required';
    }

    return errors;
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const renderFormFields = () => {
    switch (formData.type) {
      case 'hero':
        return <HeroFormFields 
          formData={formData} 
          updateFormData={updateFormData} 
          profileType={profileType}
          bookId={bookId}
        />;
      case 'about':
        return <AboutFormFields 
          formData={formData} 
          updateFormData={updateFormData} 
          profileType={profileType}
          bookId={bookId}
        />;
      case 'contact':
        return <ContactFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      case 'experience':
        return <ExperienceFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      case 'education':
        return <EducationFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      case 'skills':
        return <SkillsFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      case 'projects':
        return <ProjectsFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      case 'services':
        return <ServicesFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      case 'achievements':
        return <AchievementsFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      case 'features':
        return <FeaturesFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      case 'gallery':
        return <GalleryFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      case 'testimonials':
        return <TestimonialsFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      
      case 'references': // NEW
      return <ReferencesFormFields 
        formData={formData} 
        updateFormData={updateFormData}
        bookId={bookId}
      />;

      case 'story':
        return <StoryFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      
      case 'demo':
        return <DemoFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      
      case 'specifications':
        return <SpecificationsFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      
      case 'credentials':
        return <CredentialsFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      
      case 'pricing':
        return <PricingFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
      
      case 'engagement':
        return <EngagementFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;

      default:
        return <GenericFormFields 
          formData={formData} 
          updateFormData={updateFormData}
          bookId={bookId}
        />;
    }
  };

  const getSectionTypes = () => {
    const baseTypes = [
      { value: 'hero', label: 'Hero Section' },
      { value: 'about', label: 'About' },
      { value: 'contact', label: 'Contact' }
    ];
    
    const typesByProfile = {
      personal: [
        ...baseTypes,
        { value: 'experience', label: 'Work Experience' },
        { value: 'education', label: 'Education' },
        { value: 'skills', label: 'Skills' },
        { value: 'projects', label: 'Projects' },
        { value: 'achievements', label: 'Achievements' },
        { value: 'testimonials', label: 'Testimonials' },
        { value: 'references', label: 'References' }
      ],
      organization: [
        ...baseTypes,
        { value: 'services', label: 'Services' },
        { value: 'team', label: 'Team' },
        { value: 'testimonials', label: 'Testimonials' },
        { value: 'gallery', label: 'Gallery' },
        { value: 'references', label: 'References' },
        { value: 'testimonials', label: 'Testimonials' },
        { value: 'stats', label: 'Statistics' }
      ],
      project: [
        ...baseTypes,
        { value: 'features', label: 'Features' },
        { value: 'team', label: 'Team' },
        { value: 'technology', label: 'Technology' },
        { value: 'timeline', label: 'Timeline' },
        { value: 'references', label: 'References' },
        { value: 'testimonials', label: 'Testimonials' },
        { value: 'gallery', label: 'Gallery' }
      ],
      asset: [
        ...baseTypes,
        { value: 'specifications', label: 'Specifications' },
        { value: 'pricing', label: 'Pricing' },
        { value: 'gallery', label: 'Gallery' },
        { value: 'reviews', label: 'Reviews' }
      ],

      product: [
        ...baseTypes,
        { value: 'story', label: 'Product Story' },
        { value: 'features', label: 'Features' },
        { value: 'demo', label: 'Demo & Videos' },
        { value: 'specifications', label: 'Specifications' },
        { value: 'gallery', label: 'Gallery' },
        { value: 'credentials', label: 'Credentials' },
        { value: 'testimonials', label: 'Testimonials' },
        { value: 'pricing', label: 'Pricing' },
        { value: 'engagement', label: 'Customer Engagement' }
      ],
      
      service: [
        ...baseTypes,
        { value: 'story', label: 'Service Story' },
        { value: 'services', label: 'Services Offered' },
        { value: 'demo', label: 'Demo & Videos' },
        { value: 'gallery', label: 'Gallery' },
        { value: 'credentials', label: 'Credentials' },
        { value: 'testimonials', label: 'Testimonials' },
        { value: 'pricing', label: 'Pricing' },
        { value: 'engagement', label: 'Customer Engagement' }
      ]
    };
    
    return typesByProfile[profileType] || baseTypes;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {section ? 'Edit Section' : 'Add New Section'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-blue-700 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Basic Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => updateFormData('type', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.type ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={section} // Don't allow changing type when editing
                >
                  {getSectionTypes().map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                {errors.type && <p className="text-red-500 text-xs mt-1">{errors.type}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => updateFormData('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter section title"
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => updateFormData('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => updateFormData('order', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Dynamic Form Fields */}
            {renderFormFields()}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving...' : 'Save Section'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AboutFormFields = ({ formData, updateFormData, profileType, bookId }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {profileType === 'organization' ? 'About the Organization' :
         profileType === 'project' ? 'Project Description' :
         profileType === 'asset' ? 'Product Description' : 'About Me'}
      </label>
      <textarea
        value={formData.description || ''}
        onChange={(e) => updateFormData('description', e.target.value)}
        rows="6"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        placeholder="Write a detailed description..."
      />
    </div>

    {profileType === 'organization' && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mission Statement
          </label>
          <textarea
            value={formData.mission || ''}
            onChange={(e) => updateFormData('mission', e.target.value)}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Your organization's mission..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vision Statement
          </label>
          <textarea
            value={formData.vision || ''}
            onChange={(e) => updateFormData('vision', e.target.value)}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Your organization's vision..."
          />
        </div>
      </div>
    )}

    {/* Product Features - Only for product profileType */}
    {profileType === 'product' && (
      <>
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Key Features
            </label>
            <button
              type="button"
              onClick={() => {
                const features = formData.features || [];
                features.push('');
                updateFormData('features', features);
              }}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add Feature
            </button>
          </div>
          <div className="space-y-2">
            {(formData.features || ['']).map((feature, idx) => (
              <div key={idx} className="flex gap-2">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => {
                    const features = [...(formData.features || [])];
                    features[idx] = e.target.value;
                    updateFormData('features', features);
                  }}
                  placeholder={`Feature ${idx + 1}`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                {idx > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const features = (formData.features || []).filter((_, i) => i !== idx);
                      updateFormData('features', features);
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Product Stats */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Product Stats
            </label>
            <button
              type="button"
              onClick={() => {
                const stats = formData.stats || [];
                stats.push({ label: '', value: '' });
                updateFormData('stats', stats);
              }}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add Stat
            </button>
          </div>
          <div className="space-y-3">
            {(formData.stats || [{ label: '', value: '' }]).map((stat, idx) => (
              <div key={idx} className="flex gap-3 items-start">
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={stat.label}
                    onChange={(e) => {
                      const stats = [...(formData.stats || [])];
                      stats[idx] = { ...stats[idx], label: e.target.value };
                      updateFormData('stats', stats);
                    }}
                    placeholder="Label (e.g., Active Users)"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={stat.value}
                    onChange={(e) => {
                      const stats = [...(formData.stats || [])];
                      stats[idx] = { ...stats[idx], value: e.target.value };
                      updateFormData('stats', stats);
                    }}
                    placeholder="Value (e.g., 10K+)"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {idx > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const stats = (formData.stats || []).filter((_, i) => i !== idx);
                      updateFormData('stats', stats);
                    }}
                    className="text-red-600 hover:text-red-700 mt-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </>
    )}
  </div>
);

const ContactFormFields = ({ formData, updateFormData, bookId }) => {
  const [socialLinks, setSocialLinks] = useState(formData.socialLinks || []);

  const addSocialLink = () => {
    const newLinks = [...socialLinks, { platform: '', url: '', label: '' }];
    setSocialLinks(newLinks);
    updateFormData('socialLinks', newLinks);
  };

  const removeSocialLink = (index) => {
    const newLinks = socialLinks.filter((_, i) => i !== index);
    setSocialLinks(newLinks);
    updateFormData('socialLinks', newLinks);
  };

  const updateSocialLink = (index, field, value) => {
    const newLinks = [...socialLinks];
    newLinks[index][field] = value;
    setSocialLinks(newLinks);
    updateFormData('socialLinks', newLinks);
  };

  return (
    <div className="space-y-6">
      {/* Primary Contact Information */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Primary Contact</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => updateFormData('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="contact@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => updateFormData('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>
      </div>

      {/* Location Information */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Location</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <textarea
              value={formData.address || ''}
              onChange={(e) => updateFormData('address', e.target.value)}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="123 Main St, City, State 12345"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                value={formData.city || ''}
                onChange={(e) => updateFormData('city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="City"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State/Province
              </label>
              <input
                type="text"
                value={formData.state || ''}
                onChange={(e) => updateFormData('state', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="State"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <input
                type="text"
                value={formData.country || ''}
                onChange={(e) => updateFormData('country', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Country"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Website & Links */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Website & Links</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website URL
            </label>
            <input
              type="url"
              value={formData.website || ''}
              onChange={(e) => updateFormData('website', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://www.example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              LinkedIn Profile
            </label>
            <input
              type="url"
              value={formData.linkedin || ''}
              onChange={(e) => updateFormData('linkedin', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://linkedin.com/in/username"
            />
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-medium text-gray-900">Social Media Links</h4>
          <button
            type="button"
            onClick={addSocialLink}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Social Link
          </button>
        </div>

        <div className="space-y-3">
          {socialLinks.map((link, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 border border-gray-200 rounded-lg">
              <div>
                <select
                  value={link.platform || ''}
                  onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-sm"
                >
                  <option value="">Select Platform</option>
                  <option value="twitter">Twitter/X</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="youtube">YouTube</option>
                  <option value="tiktok">TikTok</option>
                  <option value="github">GitHub</option>
                  <option value="behance">Behance</option>
                  <option value="dribbble">Dribbble</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <input
                  type="text"
                  value={link.label || ''}
                  onChange={(e) => updateSocialLink(index, 'label', e.target.value)}
                  placeholder="Display name"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <div>
                <input
                  type="url"
                  value={link.url || ''}
                  onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                  placeholder="Profile URL"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => removeSocialLink(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Business Hours (for organizations) */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Business Hours (Optional)</h4>
        <textarea
          value={formData.businessHours || ''}
          onChange={(e) => updateFormData('businessHours', e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="Monday - Friday: 9:00 AM - 5:00 PM&#10;Saturday: 10:00 AM - 2:00 PM&#10;Sunday: Closed"
        />
      </div>

      {/* Contact Preferences */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Contact Preferences</h4>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showEmail !== false}
              onChange={(e) => updateFormData('showEmail', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show email address</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showPhone !== false}
              onChange={(e) => updateFormData('showPhone', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show phone number</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.allowDirectContact !== false}
              onChange={(e) => updateFormData('allowDirectContact', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Allow direct contact form</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.trackContactClicks === true}
              onChange={(e) => updateFormData('trackContactClicks', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Track contact interactions</span>
          </label>
        </div>
      </div>
    </div>
  );
};

const ProjectsFormFields = ({ formData, updateFormData, bookId }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <label className="block text-sm font-medium text-gray-700">
        Projects
      </label>
      <button
        type="button"
        onClick={() => {
          const projects = formData.items || [];
          projects.push({
            title: '',
            description: '',
            status: 'completed',
            url: '',
            image: '',
            technologies: []
          });
          updateFormData('items', projects);
        }}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
      >
        <Plus className="h-4 w-4" />
        Add Project
      </button>
    </div>

    {(formData.items || []).map((project, index) => (
      <div key={index} className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-start mb-4">
          <h4 className="text-sm font-medium text-gray-900">Project {index + 1}</h4>
          <button
            type="button"
            onClick={() => {
              const projects = formData.items.filter((_, i) => i !== index);
              updateFormData('items', projects);
            }}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Project Title
            </label>
            <input
              type="text"
              value={project.title}
              onChange={(e) => {
                const projects = [...formData.items];
                projects[index].title = e.target.value;
                updateFormData('items', projects);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Project name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={project.status}
              onChange={(e) => {
                const projects = [...formData.items];
                projects[index].status = e.target.value;
                updateFormData('items', projects);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="completed">Completed</option>
              <option value="in-progress">In Progress</option>
              <option value="planned">Planned</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={project.description}
            onChange={(e) => {
              const projects = [...formData.items];
              projects[index].description = e.target.value;
              updateFormData('items', projects);
            }}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Project description..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Project URL
            </label>
            <input
              type="url"
              value={project.url}
              onChange={(e) => {
                const projects = [...formData.items];
                projects[index].url = e.target.value;
                updateFormData('items', projects);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="https://project-demo.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Image URL
            </label>
            <input
              type="url"
              value={project.image}
              onChange={(e) => {
                const projects = [...formData.items];
                projects[index].image = e.target.value;
                updateFormData('items', projects);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="https://example.com/screenshot.jpg"
            />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Social Links Editor Component
const SocialLinksEditor = ({ socialLinks, onChange, bookId }) => {
  const [newPlatform, setNewPlatform] = useState('');
  const [newUrl, setNewUrl] = useState('');

  const platforms = [
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'twitter', label: 'Twitter' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'github', label: 'GitHub' },
    { value: 'youtube', label: 'YouTube' }
  ];

  const addSocialLink = () => {
    if (newPlatform && newUrl) {
      onChange({
        ...socialLinks,
        [newPlatform]: newUrl
      });
      setNewPlatform('');
      setNewUrl('');
    }
  };

  const removeSocialLink = (platform) => {
    const updatedLinks = { ...socialLinks };
    delete updatedLinks[platform];
    onChange(updatedLinks);
  };

  return (
    <div className="space-y-3">
      {Object.entries(socialLinks || {}).map(([platform, url]) => (
        <div key={platform} className="flex items-center gap-3">
          <span className="text-sm font-medium capitalize text-gray-700 min-w-[80px]">
            {platform}:
          </span>
          <input
            type="url"
            value={url}
            onChange={(e) => onChange({ ...socialLinks, [platform]: e.target.value })}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <button
            type="button"
            onClick={() => removeSocialLink(platform)}
            className="text-red-600 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}

      <div className="flex gap-2">
        <select
          value={newPlatform}
          onChange={(e) => setNewPlatform(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
        >
          <option value="">Select Platform</option>
          {platforms
            .filter(p => !socialLinks[p.value])
            .map(platform => (
              <option key={platform.value} value={platform.value}>
                {platform.label}
              </option>
            ))}
        </select>
        
        <input
          type="url"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="Profile URL"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
        />
        
        <button
          type="button"
          onClick={addSocialLink}
          disabled={!newPlatform || !newUrl}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const ExperienceFormFields = ({ formData, updateFormData, bookId }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <label className="block text-sm font-medium text-gray-700">
        Work Experience
      </label>
      <button
        type="button"
        onClick={() => {
          const experience = formData.items || [];
          experience.push({
            company: '',
            position: '',
            startDate: '',
            endDate: '',
            current: false,
            description: '',
            achievements: []
          });
          updateFormData('items', experience);
        }}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
      >
        <Plus className="h-4 w-4" />
        Add Experience
      </button>
    </div>

    {(formData.items || []).map((experience, index) => (
      <div key={index} className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-start mb-4">
          <h4 className="text-sm font-medium text-gray-900">Experience {index + 1}</h4>
          <button
            type="button"
            onClick={() => {
              const experiences = formData.items.filter((_, i) => i !== index);
              updateFormData('items', experiences);
            }}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Company
            </label>
            <input
              type="text"
              value={experience.company}
              onChange={(e) => {
                const experiences = [...formData.items];
                experiences[index].company = e.target.value;
                updateFormData('items', experiences);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Company name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Position
            </label>
            <input
              type="text"
              value={experience.position}
              onChange={(e) => {
                const experiences = [...formData.items];
                experiences[index].position = e.target.value;
                updateFormData('items', experiences);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Job title"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={experience.startDate}
              onChange={(e) => {
                const experiences = [...formData.items];
                experiences[index].startDate = e.target.value;
                updateFormData('items', experiences);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={experience.endDate}
              onChange={(e) => {
                const experiences = [...formData.items];
                experiences[index].endDate = e.target.value;
                updateFormData('items', experiences);
              }}
              disabled={experience.current}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100"
            />
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={experience.current || false}
                onChange={(e) => {
                  const experiences = [...formData.items];
                  experiences[index].current = e.target.checked;
                  if (e.target.checked) {
                    experiences[index].endDate = '';
                  }
                  updateFormData('items', experiences);
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Current Role</span>
            </label>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={experience.description}
            onChange={(e) => {
              const experiences = [...formData.items];
              experiences[index].description = e.target.value;
              updateFormData('items', experiences);
            }}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Describe your role and responsibilities..."
          />
        </div>
      </div>
    ))}
  </div>
);

const ServicesFormFields = ({ formData, updateFormData, bookId }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <label className="block text-sm font-medium text-gray-700">
        Services & Products
      </label>
      <button
        type="button"
        onClick={() => {
          const services = formData.items || [];
          services.push({
            title: '',
            description: '',
            price: '',
            features: [],
            image: '',
            category: ''
          });
          updateFormData('items', services);
        }}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
      >
        <Plus className="h-4 w-4" />
        Add Service
      </button>
    </div>

    {(formData.items || []).map((service, index) => (
      <div key={index} className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-start mb-4">
          <h4 className="text-sm font-medium text-gray-900">Service {index + 1}</h4>
          <button
            type="button"
            onClick={() => {
              const services = formData.items.filter((_, i) => i !== index);
              updateFormData('items', services);
            }}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Service Title
            </label>
            <input
              type="text"
              value={service.title}
              onChange={(e) => {
                const services = [...formData.items];
                services[index].title = e.target.value;
                updateFormData('items', services);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Service or product name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Price
            </label>
            <input
              type="text"
              value={service.price}
              onChange={(e) => {
                const services = [...formData.items];
                services[index].price = e.target.value;
                updateFormData('items', services);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="$99.99 or From $50"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={service.description}
            onChange={(e) => {
              const services = [...formData.items];
              services[index].description = e.target.value;
              updateFormData('items', services);
            }}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Describe the service or product..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Category
            </label>
            <input
              type="text"
              value={service.category}
              onChange={(e) => {
                const services = [...formData.items];
                services[index].category = e.target.value;
                updateFormData('items', services);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Web Development, Consulting, etc."
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Image URL
            </label>
            <input
              type="url"
              value={service.image}
              onChange={(e) => {
                const services = [...formData.items];
                services[index].image = e.target.value;
                updateFormData('items', services);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="https://example.com/service-image.jpg"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Key Features (comma separated)
          </label>
          <input
            type="text"
            value={service.features?.join(', ') || ''}
            onChange={(e) => {
              const services = [...formData.items];
              services[index].features = e.target.value.split(',').map(f => f.trim()).filter(f => f);
              updateFormData('items', services);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Fast delivery, 24/7 support, money-back guarantee"
          />
        </div>
      </div>
    ))}
  </div>
);

const AchievementsFormFields = ({ formData, updateFormData, bookId }) => {
  const [uploadStates, setUploadStates] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  const handleBadgeUpload = async (file, error, achievementIndex) => {
    if (error) {
      setUploadErrors(prev => ({ ...prev, [achievementIndex]: error }));
      return;
    }

    setUploadStates(prev => ({ ...prev, [achievementIndex]: true }));
    setUploadErrors(prev => ({ ...prev, [achievementIndex]: '' }));

    try {
      const bookId = formData.bookId || 'temp';
      const result = await profileService.uploadProfileMedia(file, bookId, 'achievements', achievementIndex);
      
      const achievements = [...formData.items];
      achievements[achievementIndex].imageUrl = result.url;
      achievements[achievementIndex].imageFileName = result.fileName;
      updateFormData('items', achievements);
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, [achievementIndex]: error.message }));
    } finally {
      setUploadStates(prev => ({ ...prev, [achievementIndex]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Achievements & Awards
        </label>
        <button
          type="button"
          onClick={() => {
            const achievements = formData.items || [];
            achievements.push({
              title: '',
              organization: '',
              date: '',
              description: '',
              credentialUrl: '',
              imageUrl: '',
              imageFileName: '',
              isVerified: false
            });
            updateFormData('items', achievements);
          }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Achievement
        </button>
      </div>

      {(formData.items || []).map((achievement, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-4">
            <h4 className="text-sm font-medium text-gray-900">Achievement {index + 1}</h4>
            <button
              type="button"
              onClick={() => {
                const achievements = formData.items.filter((_, i) => i !== index);
                updateFormData('items', achievements);
              }}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Achievement Title
              </label>
              <input
                type="text"
                value={achievement.title}
                onChange={(e) => {
                  const achievements = [...formData.items];
                  achievements[index].title = e.target.value;
                  updateFormData('items', achievements);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Award or certification name"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Organization
              </label>
              <input
                type="text"
                value={achievement.organization}
                onChange={(e) => {
                  const achievements = [...formData.items];
                  achievements[index].organization = e.target.value;
                  updateFormData('items', achievements);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Issuing organization"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={achievement.description}
              onChange={(e) => {
                const achievements = [...formData.items];
                achievements[index].description = e.target.value;
                updateFormData('items', achievements);
              }}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Achievement description..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Date Received
              </label>
              <input
                type="date"
                value={achievement.date}
                onChange={(e) => {
                  const achievements = [...formData.items];
                  achievements[index].date = e.target.value;
                  updateFormData('items', achievements);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Credential URL
              </label>
              <input
                type="url"
                value={achievement.credentialUrl}
                onChange={(e) => {
                  const achievements = [...formData.items];
                  achievements[index].credentialUrl = e.target.value;
                  updateFormData('items', achievements);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="https://credential-url.com"
              />
            </div>
          </div>

          {/* Badge/Certificate Image Upload */}
          <div className="mt-4">
            <FileUpload
              label="Badge/Certificate Image"
              currentFile={achievement.imageUrl}
              onFileSelect={(file, error) => handleBadgeUpload(file, error, index)}
              onRemove={() => {
                const achievements = [...formData.items];
                achievements[index].imageUrl = '';
                achievements[index].imageFileName = '';
                updateFormData('items', achievements);
              }}
              accept="image/*,.pdf"
              maxSize={5}
              uploading={uploadStates[index]}
              error={uploadErrors[index]}
              showPreview={true}
            />
          </div>

          <div className="mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={achievement.isVerified || false}
                onChange={(e) => {
                  const achievements = [...formData.items];
                  achievements[index].isVerified = e.target.checked;
                  updateFormData('items', achievements);
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Verified Achievement</span>
            </label>
          </div>
        </div>
      ))}
    </div>
  );
};

const SkillsFormFields = ({ formData, updateFormData, bookId }) => {
  const [uploadStates, setUploadStates] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  const handleSkillCertificationUpload = async (file, error, categoryIndex, skillIndex) => {
    const key = `${categoryIndex}-${skillIndex}`;
    
    if (error) {
      setUploadErrors(prev => ({ ...prev, [key]: error }));
      return;
    }

    setUploadStates(prev => ({ ...prev, [key]: true }));
    setUploadErrors(prev => ({ ...prev, [key]: '' }));

    try {
      const bookId = formData.bookId || 'temp';
      const result = await profileService.uploadProfileMedia(file, bookId, 'skills', key);
      
      const categories = [...formData.categories];
      categories[categoryIndex].skills[skillIndex].certificationUrl = result.url;
      categories[categoryIndex].skills[skillIndex].certificationFileName = result.fileName;
      updateFormData('categories', categories);
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, [key]: error.message }));
    } finally {
      setUploadStates(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Skills & Expertise
        </label>
        <button
          type="button"
          onClick={() => {
            const categories = formData.categories || [];
            categories.push({
              name: '',
              skills: []
            });
            updateFormData('categories', categories);
          }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Category
        </button>
      </div>

      {(formData.categories || []).map((category, categoryIndex) => (
        <div key={categoryIndex} className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <input
                type="text"
                value={category.name}
                onChange={(e) => {
                  const categories = [...formData.categories];
                  categories[categoryIndex].name = e.target.value;
                  updateFormData('categories', categories);
                }}
                className="text-sm font-medium text-gray-900 border-none bg-transparent focus:ring-0 px-0"
                placeholder="Category name (e.g., Programming, Design)"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const categories = formData.categories.filter((_, i) => i !== categoryIndex);
                updateFormData('categories', categories);
              }}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          {/* Individual Skills with Certification */}
          <div className="space-y-3">
            {(category.skills || []).map((skill, skillIndex) => (
              <div key={skillIndex} className="bg-gray-50 rounded-lg p-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <input
                      type="text"
                      value={skill.name}
                      onChange={(e) => {
                        const categories = [...formData.categories];
                        categories[categoryIndex].skills[skillIndex].name = e.target.value;
                        updateFormData('categories', categories);
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                      placeholder="Skill name"
                    />
                  </div>
                  
                  <div>
                    <select
                      value={skill.level || 3}
                      onChange={(e) => {
                        const categories = [...formData.categories];
                        categories[categoryIndex].skills[skillIndex].level = parseInt(e.target.value);
                        updateFormData('categories', categories);
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    >
                      <option value={1}>Beginner</option>
                      <option value={2}>Intermediate</option>
                      <option value={3}>Advanced</option>
                      <option value={4}>Expert</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const categories = [...formData.categories];
                        categories[categoryIndex].skills = categories[categoryIndex].skills.filter((_, i) => i !== skillIndex);
                        updateFormData('categories', categories);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>

                {/* Skill Certification Upload */}
                <div className="mt-3">
                  <FileUpload
                    label={`${skill.name || 'Skill'} Certification`}
                    currentFile={skill.certificationUrl}
                    onFileSelect={(file, error) => handleSkillCertificationUpload(file, error, categoryIndex, skillIndex)}
                    onRemove={() => {
                      const categories = [...formData.categories];
                      categories[categoryIndex].skills[skillIndex].certificationUrl = '';
                      categories[categoryIndex].skills[skillIndex].certificationFileName = '';
                      updateFormData('categories', categories);
                    }}
                    accept="image/*,.pdf"
                    maxSize={10}
                    uploading={uploadStates[`${categoryIndex}-${skillIndex}`]}
                    error={uploadErrors[`${categoryIndex}-${skillIndex}`]}
                    showPreview={true}
                  />
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={() => {
                const categories = [...formData.categories];
                categories[categoryIndex].skills.push({
                  name: '',
                  level: 3,
                  certificationUrl: '',
                  certificationFileName: ''
                });
                updateFormData('categories', categories);
              }}
              className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-3 w-3" />
              Add Skill
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const EducationFormFields = ({ formData, updateFormData, bookId }) => {
  const [uploadStates, setUploadStates] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  const handleCertificationUpload = async (file, error, eduIndex) => {
    if (error) {
      setUploadErrors(prev => ({ ...prev, [eduIndex]: error }));
      return;
    }

    setUploadStates(prev => ({ ...prev, [eduIndex]: true }));
    setUploadErrors(prev => ({ ...prev, [eduIndex]: '' }));

    try {
      const bookId = formData.bookId || 'temp';
      const result = await profileService.uploadProfileMedia(file, bookId, 'education', eduIndex);
      
      const education = [...formData.items];
      education[eduIndex].certificationUrl = result.url;
      education[eduIndex].certificationFileName = result.fileName;
      updateFormData('items', education);
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, [eduIndex]: error.message }));
    } finally {
      setUploadStates(prev => ({ ...prev, [eduIndex]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Education
        </label>
        <button
          type="button"
          onClick={() => {
            const education = formData.items || [];
            education.push({
              institution: '',
              degree: '',
              field: '',
              startDate: '',
              endDate: '',
              gpa: '',
              achievements: [],
              certificationUrl: '',
              certificationFileName: ''
            });
            updateFormData('items', education);
          }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Education
        </button>
      </div>

      {(formData.items || []).map((edu, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-4">
            <h4 className="text-sm font-medium text-gray-900">Education {index + 1}</h4>
            <button
              type="button"
              onClick={() => {
                const education = formData.items.filter((_, i) => i !== index);
                updateFormData('items', education);
              }}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Institution
              </label>
              <input
                type="text"
                value={edu.institution}
                onChange={(e) => {
                  const education = [...formData.items];
                  education[index].institution = e.target.value;
                  updateFormData('items', education);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="University or school name"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Qualification
              </label>
              <input
                type="text"
                value={edu.qualification}
                onChange={(e) => {
                  const education = [...formData.items];
                  education[index].qualification = e.target.value;
                  updateFormData('items', education);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Bachelor's, Master's, PhD, etc."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Field of Study
              </label>
              <input
                type="text"
                value={edu.field}
                onChange={(e) => {
                  const education = [...formData.items];
                  education[index].field = e.target.value;
                  updateFormData('items', education);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Computer Science, Business, etc."
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={edu.startDate}
                onChange={(e) => {
                  const education = [...formData.items];
                  education[index].startDate = e.target.value;
                  updateFormData('items', education);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={edu.endDate}
                onChange={(e) => {
                  const education = [...formData.items];
                  education[index].endDate = e.target.value;
                  updateFormData('items', education);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Certification Upload */}
          <div className="mt-4">
            <FileUpload
              label="Diploma/Certificate"
              currentFile={edu.certificationUrl}
              onFileSelect={(file, error) => handleCertificationUpload(file, error, index)}
              onRemove={() => {
                const education = [...formData.items];
                education[index].certificationUrl = '';
                education[index].certificationFileName = '';
                updateFormData('items', education);
              }}
              accept="image/*,.pdf"
              maxSize={10}
              uploading={uploadStates[index]}
              error={uploadErrors[index]}
              showPreview={true}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const FeaturesFormFields = ({ formData, updateFormData, bookId }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <label className="block text-sm font-medium text-gray-700">
        Key Features
      </label>
      <button
        type="button"
        onClick={() => {
          const features = formData.items || [];
          features.push({
            title: '',
            description: '',
            status: 'completed'
          });
          updateFormData('items', features);
        }}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
      >
        <Plus className="h-4 w-4" />
        Add Feature
      </button>
    </div>

    {(formData.items || []).map((feature, index) => (
      <div key={index} className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-start mb-4">
          <h4 className="text-sm font-medium text-gray-900">Feature {index + 1}</h4>
          <button
            type="button"
            onClick={() => {
              const features = formData.items.filter((_, i) => i !== index);
              updateFormData('items', features);
            }}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Feature Title
            </label>
            <input
              type="text"
              value={feature.title}
              onChange={(e) => {
                const features = [...formData.items];
                features[index].title = e.target.value;
                updateFormData('items', features);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Feature name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={feature.status}
              onChange={(e) => {
                const features = [...formData.items];
                features[index].status = e.target.value;
                updateFormData('items', features);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="completed">Completed</option>
              <option value="in-progress">In Progress</option>
              <option value="planned">Planned</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={feature.description}
            onChange={(e) => {
              const features = [...formData.items];
              features[index].description = e.target.value;
              updateFormData('items', features);
            }}
            rows="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Feature description..."
          />
        </div>
      </div>
    ))}
  </div>
);

const GalleryFormFields = ({ formData, updateFormData, bookId }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadErrors, setUploadErrors] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  // Handle multiple file uploads at once
  const handleBulkFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setBulkUploading(true);
    setUploadProgress(0);
    setUploadErrors({});

    try {
      const results = await profileService.uploadMultipleFiles(
        Array.from(files), 
        bookId, 
        'gallery',
        (completed, total) => {
          setUploadProgress(Math.round((completed / total) * 100));
        }
      );

      // Process successful uploads
      const currentItems = formData.items || [];
      const newItems = results
        .filter(result => result.success)
        .map((result, index) => ({
          id: Date.now() + index,
          title: '',
          description: '',
          url: result.url,
          fileName: result.fileName,
          fileType: result.fileType,
          type: result.fileType.startsWith('image/') ? 'image' : 'video',
          order: currentItems.length + index,
          uploadedAt: new Date().toISOString()
        }));

      // Handle errors
      const errors = results.filter(result => !result.success);
      if (errors.length > 0) {
        const errorMessages = {};
        errors.forEach(error => {
          errorMessages[error.fileName] = error.error;
        });
        setUploadErrors(errorMessages);
      }

      updateFormData('items', [...currentItems, ...newItems]);
    } catch (error) {
      console.error('Error uploading gallery files:', error);
      setUploadErrors({ general: error.message });
    } finally {
      setBulkUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle single file upload for existing items
  const handleSingleFileUpload = async (file, error, itemIndex) => {
    if (error) {
      setUploadErrors(prev => ({ ...prev, [itemIndex]: error }));
      return;
    }

    if (!file) {
      const items = [...formData.items];
      items[itemIndex].url = '';
      items[itemIndex].fileName = '';
      items[itemIndex].fileType = '';
      updateFormData('items', items);
      return;
    }

    setUploading(true);
    setUploadErrors(prev => ({ ...prev, [itemIndex]: '' }));

    try {
      const result = await profileService.uploadProfileMedia(file, bookId, 'gallery', itemIndex);
      
      const items = [...formData.items];
      items[itemIndex] = {
        ...items[itemIndex],
        url: result.url,
        fileName: result.fileName,
        fileType: result.fileType,
        type: result.fileType.startsWith('image/') ? 'image' : 'video'
      };
      updateFormData('items', items);
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, [itemIndex]: error.message }));
    } finally {
      setUploading(false);
    }
  };

  // Handle drag and drop reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const items = [...formData.items];
    const draggedItem = items[draggedIndex];
    
    // Remove dragged item
    items.splice(draggedIndex, 1);
    
    // Insert at new position
    const insertIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
    items.splice(insertIndex, 0, draggedItem);
    
    // Update order values
    const reorderedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    updateFormData('items', reorderedItems);
    setDraggedIndex(null);
  };

  const removeItem = (index) => {
    const items = formData.items.filter((_, i) => i !== index);
    updateFormData('items', items);
  };

  const addEmptyItem = () => {
    const items = formData.items || [];
    items.push({
      id: Date.now(),
      title: '',
      description: '',
      url: '',
      fileName: '',
      fileType: '',
      type: 'image',
      order: items.length
    });
    updateFormData('items', items);
  };

  return (
    <div className="space-y-6">
      {/* Header with Bulk Upload */}
      <div className="flex justify-between items-start">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Gallery Items
          </label>
          <p className="text-xs text-gray-500">
            Upload multiple images/videos or add items individually
          </p>
        </div>
        
        <div className="flex gap-2">
          <FileUpload
            label=""
            onFileSelect={handleBulkFileUpload}
            accept="image/*,video/*"
            maxSize={10}
            uploading={bulkUploading}
            error={uploadErrors.general}
            showPreview={false}
            multiple={true}
          />
          
          <button
            type="button"
            onClick={addEmptyItem}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 px-3 py-2 border border-blue-200 rounded-lg hover:bg-blue-50"
          >
            <Plus className="h-4 w-4" />
            Add Item
          </button>
        </div>
      </div>

      {/* Bulk Upload Progress */}
      {bulkUploading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Uploading files...</span>
            <span className="text-sm text-blue-700">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Upload Errors */}
      {Object.keys(uploadErrors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-red-900 mb-2">Upload Errors:</h4>
          <ul className="text-sm text-red-700 space-y-1">
            {Object.entries(uploadErrors).map(([key, error]) => (
              <li key={key}>• {key}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Gallery Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(formData.items || []).map((item, index) => (
          <div
            key={item.id || index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            className={`border-2 rounded-lg p-4 cursor-move transition-all ${
              draggedIndex === index 
                ? 'border-blue-400 bg-blue-50 scale-105' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {/* Item Header */}
            <div className="flex justify-between items-start mb-3">
              <h4 className="text-sm font-medium text-gray-900">Item {index + 1}</h4>
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {/* File Upload/Preview */}
            <div className="mb-4">
              {item.url ? (
                <div className="relative">
                  {item.type === 'image' || item.fileType?.startsWith('image/') ? (
                    <img
                      src={item.url}
                      alt={item.title || `Gallery item ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  ) : item.type === 'video' || item.fileType?.startsWith('video/') ? (
                    <video
                      src={item.url}
                      className="w-full h-32 object-cover rounded-lg"
                      controls
                    />
                  ) : (
                    <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                      <File className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  
                  <button
                    type="button"
                    onClick={() => {
                      const items = [...formData.items];
                      items[index].url = '';
                      items[index].fileName = '';
                      items[index].fileType = '';
                      updateFormData('items', items);
                    }}
                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <FileUpload
                  label=""
                  currentFile={null}
                  onFileSelect={(file, error) => handleSingleFileUpload(file, error, index)}
                  onRemove={() => {}}
                  accept="image/*,video/*"
                  maxSize={10}
                  uploading={uploading}
                  error={uploadErrors[index]}
                  showPreview={false}
                />
              )}
            </div>

            {/* Item Details */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={item.title || ''}
                  onChange={(e) => {
                    const items = [...formData.items];
                    items[index].title = e.target.value;
                    updateFormData('items', items);
                  }}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholder="Item title"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={item.description || ''}
                  onChange={(e) => {
                    const items = [...formData.items];
                    items[index].description = e.target.value;
                    updateFormData('items', items);
                  }}
                  rows="2"
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholder="Item description"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={item.tags?.join(', ') || ''}
                  onChange={(e) => {
                    const items = [...formData.items];
                    items[index].tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                    updateFormData('items', items);
                  }}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholder="portfolio, photography, design"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={item.type || 'image'}
                    onChange={(e) => {
                      const items = [...formData.items];
                      items[index].type = e.target.value;
                      updateFormData('items', items);
                    }}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="document">Document</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Order
                  </label>
                  <input
                    type="number"
                    value={item.order || index}
                    onChange={(e) => {
                      const items = [...formData.items];
                      items[index].order = parseInt(e.target.value) || 0;
                      updateFormData('items', items);
                    }}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {(!formData.items || formData.items.length === 0) && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">No gallery items yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Upload multiple files at once or add items individually
          </p>
          <button
            type="button"
            onClick={addEmptyItem}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Add your first item
          </button>
        </div>
      )}

      {/* Gallery Settings */}
      <div className="border-t border-gray-200 pt-6 mt-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">Gallery Settings</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Layout Style
            </label>
            <select
              value={formData.layout || 'grid'}
              onChange={(e) => updateFormData('layout', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="grid">Grid</option>
              <option value="masonry">Masonry</option>
              <option value="carousel">Carousel</option>
              <option value="list">List</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Items per Row
            </label>
            <select
              value={formData.columns || 3}
              onChange={(e) => updateFormData('columns', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value={2}>2 columns</option>
              <option value={3}>3 columns</option>
              <option value={4}>4 columns</option>
              <option value={5}>5 columns</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Image Size
            </label>
            <select
              value={formData.imageSize || 'medium'}
              onChange={(e) => updateFormData('imageSize', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showTitles || false}
              onChange={(e) => updateFormData('showTitles', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show item titles</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showDescriptions || false}
              onChange={(e) => updateFormData('showDescriptions', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show item descriptions</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.enableLightbox || true}
              onChange={(e) => updateFormData('enableLightbox', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Enable lightbox viewer</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.enableDownload || false}
              onChange={(e) => updateFormData('enableDownload', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Allow downloads</span>
          </label>
        </div>
      </div>
    </div>
  );
};

const TestimonialsFormFields = ({ formData, updateFormData, bookId }) => {
  const [uploadStates, setUploadStates] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  const handleClientPhotoUpload = async (file, error, testimonialIndex) => {
    if (error) {
      setUploadErrors(prev => ({ ...prev, [testimonialIndex]: error }));
      return;
    }

    setUploadStates(prev => ({ ...prev, [testimonialIndex]: true }));
    setUploadErrors(prev => ({ ...prev, [testimonialIndex]: '' }));

    try {
      const result = await profileService.uploadProfileMedia(file, bookId, 'testimonials', testimonialIndex);
      
      const testimonials = [...formData.items];
      testimonials[testimonialIndex].clientPhoto = result.url;
      testimonials[testimonialIndex].clientPhotoFileName = result.fileName;
      updateFormData('items', testimonials);
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, [testimonialIndex]: error.message }));
    } finally {
      setUploadStates(prev => ({ ...prev, [testimonialIndex]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Client Testimonials & Reviews
        </label>
        <button
          type="button"
          onClick={() => {
            const testimonials = formData.items || [];
            testimonials.push({
              id: Date.now(),
              clientName: '',
              clientTitle: '',
              clientCompany: '',
              clientPhoto: '',
              clientPhotoFileName: '',
              testimonial: '',
              rating: 5,
              date: '',
              projectTitle: '',
              serviceCategory: '',
              isVerified: false,
              showOnWebsite: true,
              contactPermission: false,
              order: testimonials.length
            });
            updateFormData('items', testimonials);
          }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Testimonial
        </button>
      </div>

      {(formData.items || []).map((testimonial, index) => (
        <div key={testimonial.id || index} className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-start mb-4">
            <h4 className="text-sm font-medium text-gray-900">Testimonial {index + 1}</h4>
            <button
              type="button"
              onClick={() => {
                const testimonials = formData.items.filter((_, i) => i !== index);
                updateFormData('items', testimonials);
              }}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          {/* Client Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Client Name *
                </label>
                <input
                  type="text"
                  value={testimonial.clientName || ''}
                  onChange={(e) => {
                    const testimonials = [...formData.items];
                    testimonials[index].clientName = e.target.value;
                    updateFormData('items', testimonials);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Client Title/Position
                </label>
                <input
                  type="text"
                  value={testimonial.clientTitle || ''}
                  onChange={(e) => {
                    const testimonials = [...formData.items];
                    testimonials[index].clientTitle = e.target.value;
                    updateFormData('items', testimonials);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="CEO, Marketing Director, etc."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Company/Organization
                </label>
                <input
                  type="text"
                  value={testimonial.clientCompany || ''}
                  onChange={(e) => {
                    const testimonials = [...formData.items];
                    testimonials[index].clientCompany = e.target.value;
                    updateFormData('items', testimonials);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="ABC Company"
                />
              </div>
            </div>

            {/* Client Photo */}
            <div>
              <FileUpload
                label="Client Photo (Optional)"
                currentFile={testimonial.clientPhoto}
                onFileSelect={(file, error) => handleClientPhotoUpload(file, error, index)}
                onRemove={() => {
                  const testimonials = [...formData.items];
                  testimonials[index].clientPhoto = '';
                  testimonials[index].clientPhotoFileName = '';
                  updateFormData('items', testimonials);
                }}
                accept="image/*"
                maxSize={5}
                uploading={uploadStates[index]}
                error={uploadErrors[index]}
                showPreview={true}
              />
            </div>

            {/* Testimonial Text */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Testimonial Text *
              </label>
              <textarea
                value={testimonial.testimonial || ''}
                onChange={(e) => {
                  const testimonials = [...formData.items];
                  testimonials[index].testimonial = e.target.value;
                  updateFormData('items', testimonials);
                }}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="The testimonial text goes here..."
              />
            </div>

            {/* Rating and Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Rating
                </label>
                <select
                  value={testimonial.rating || 5}
                  onChange={(e) => {
                    const testimonials = [...formData.items];
                    testimonials[index].rating = parseInt(e.target.value);
                    updateFormData('items', testimonials);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value={5}>5 Stars - Excellent</option>
                  <option value={4}>4 Stars - Very Good</option>
                  <option value={3}>3 Stars - Good</option>
                  <option value={2}>2 Stars - Fair</option>
                  <option value={1}>1 Star - Poor</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Date Received
                </label>
                <input
                  type="date"
                  value={testimonial.date || ''}
                  onChange={(e) => {
                    const testimonials = [...formData.items];
                    testimonials[index].date = e.target.value;
                    updateFormData('items', testimonials);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            {/* Project/Service Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Project/Service Title
                </label>
                <input
                  type="text"
                  value={testimonial.projectTitle || ''}
                  onChange={(e) => {
                    const testimonials = [...formData.items];
                    testimonials[index].projectTitle = e.target.value;
                    updateFormData('items', testimonials);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Website redesign, Marketing campaign, etc."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Service Category
                </label>
                <select
                  value={testimonial.serviceCategory || ''}
                  onChange={(e) => {
                    const testimonials = [...formData.items];
                    testimonials[index].serviceCategory = e.target.value;
                    updateFormData('items', testimonials);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">Select Category</option>
                  <option value="web-development">Web Development</option>
                  <option value="marketing">Marketing</option>
                  <option value="design">Design</option>
                  <option value="consulting">Consulting</option>
                  <option value="software-development">Software Development</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="seo">SEO</option>
                  <option value="branding">Branding</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Settings and Permissions */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h5 className="text-xs font-medium text-gray-900 mb-3">Display Settings</h5>
              
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={testimonial.isVerified || false}
                    onChange={(e) => {
                      const testimonials = [...formData.items];
                      testimonials[index].isVerified = e.target.checked;
                      updateFormData('items', testimonials);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Verified Testimonial</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={testimonial.showOnWebsite !== false}
                    onChange={(e) => {
                      const testimonials = [...formData.items];
                      testimonials[index].showOnWebsite = e.target.checked;
                      updateFormData('items', testimonials);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Show on website</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={testimonial.contactPermission || false}
                    onChange={(e) => {
                      const testimonials = [...formData.items];
                      testimonials[index].contactPermission = e.target.checked;
                      updateFormData('items', testimonials);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Allow potential clients to contact this client</span>
                </label>
              </div>
            </div>

            {/* Display Order */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={testimonial.order || index}
                  onChange={(e) => {
                    const testimonials = [...formData.items];
                    testimonials[index].order = parseInt(e.target.value) || 0;
                    updateFormData('items', testimonials);
                  }}
                  className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Star Rating Preview */}
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-4 w-4 ${
                      star <= (testimonial.rating || 5)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Empty State */}
      {(!formData.items || formData.items.length === 0) && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">No testimonials yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Add client testimonials to build trust with potential customers
          </p>
          <button
            type="button"
            onClick={() => {
              const testimonials = [{
                id: Date.now(),
                clientName: '',
                clientTitle: '',
                clientCompany: '',
                clientPhoto: '',
                testimonial: '',
                rating: 5,
                date: '',
                projectTitle: '',
                serviceCategory: '',
                isVerified: false,
                showOnWebsite: true,
                contactPermission: false,
                order: 0
              }];
              updateFormData('items', testimonials);
            }}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Add your first testimonial
          </button>
        </div>
      )}

      {/* Testimonials Settings */}
      <div className="border-t border-gray-200 pt-6 mt-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">Testimonials Display Settings</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Layout Style
            </label>
            <select
              value={formData.layout || 'cards'}
              onChange={(e) => updateFormData('layout', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="cards">Card Layout</option>
              <option value="quotes">Quote Style</option>
              <option value="carousel">Carousel</option>
              <option value="grid">Grid Layout</option>
              <option value="masonry">Masonry</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Items per Row
            </label>
            <select
              value={formData.columns || 2}
              onChange={(e) => updateFormData('columns', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value={1}>1 column</option>
              <option value={2}>2 columns</option>
              <option value={3}>3 columns</option>
            </select>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showClientPhotos !== false}
              onChange={(e) => updateFormData('showClientPhotos', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show client photos</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showRatings !== false}
              onChange={(e) => updateFormData('showRatings', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show star ratings</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showCompanyInfo !== false}
              onChange={(e) => updateFormData('showCompanyInfo', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show client company information</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showProjectDetails === true}
              onChange={(e) => updateFormData('showProjectDetails', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show project/service details</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.enableFiltering === true}
              onChange={(e) => updateFormData('enableFiltering', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Enable filtering by service category</span>
          </label>
        </div>
      </div>
    </div>
  );
};

const ReferencesFormFields = ({ formData, updateFormData, bookId }) => {
  const [uploadStates, setUploadStates] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  const handleDocumentUpload = async (file, error, referenceIndex) => {
    if (error) {
      setUploadErrors(prev => ({ ...prev, [referenceIndex]: error }));
      return;
    }

    setUploadStates(prev => ({ ...prev, [referenceIndex]: true }));
    setUploadErrors(prev => ({ ...prev, [referenceIndex]: '' }));

    try {
      const result = await profileService.uploadProfileMedia(
        file, 
        bookId || 'temp', 
        'references', 
        referenceIndex
      );
      
      const references = [...(formData.items || [])];
      references[referenceIndex].documentUrl = result.url;
      references[referenceIndex].documentFileName = result.fileName;
      updateFormData('items', references);
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, [referenceIndex]: error.message }));
    } finally {
      setUploadStates(prev => ({ ...prev, [referenceIndex]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h4 className="text-md font-medium text-gray-900">References</h4>
        <button
          type="button"
          onClick={() => {
            const newReference = {
              id: Date.now(),
              name: '',
              title: '',
              company: '',
              relationship: '',
              email: '',
              phone: '',
              description: '',
              documentUrl: '',
              documentFileName: '',
              canContact: true,
              isLinkedIn: false,
              linkedInUrl: ''
            };
            updateFormData('items', [...(formData.items || []), newReference]);
          }}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Reference
        </button>
      </div>

      {(formData.items || []).map((reference, index) => (
        <div key={reference.id || index} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h5 className="font-medium text-gray-900">Reference {index + 1}</h5>
            <button
              type="button"
              onClick={() => {
                const references = formData.items.filter((_, i) => i !== index);
                updateFormData('items', references);
              }}
              className="text-red-500 hover:text-red-700 p-1"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Reference Name */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={reference.name}
                onChange={(e) => {
                  const references = [...formData.items];
                  references[index].name = e.target.value;
                  updateFormData('items', references);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="John Smith"
              />
            </div>

            {/* Title/Position */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Title/Position
              </label>
              <input
                type="text"
                value={reference.title}
                onChange={(e) => {
                  const references = [...formData.items];
                  references[index].title = e.target.value;
                  updateFormData('items', references);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Senior Manager"
              />
            </div>

            {/* Company */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Company/Organization
              </label>
              <input
                type="text"
                value={reference.company}
                onChange={(e) => {
                  const references = [...formData.items];
                  references[index].company = e.target.value;
                  updateFormData('items', references);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="ABC Corporation"
              />
            </div>

            {/* Relationship */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Relationship
              </label>
              <select
                value={reference.relationship}
                onChange={(e) => {
                  const references = [...formData.items];
                  references[index].relationship = e.target.value;
                  updateFormData('items', references);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Select relationship</option>
                <option value="supervisor">Direct Supervisor</option>
                <option value="colleague">Colleague</option>
                <option value="client">Client</option>
                <option value="mentor">Mentor</option>
                <option value="professor">Professor</option>
                <option value="manager">Manager</option>
                <option value="business-partner">Business Partner</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={reference.email}
                onChange={(e) => {
                  const references = [...formData.items];
                  references[index].email = e.target.value;
                  updateFormData('items', references);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="john@company.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={reference.phone}
                onChange={(e) => {
                  const references = [...formData.items];
                  references[index].phone = e.target.value;
                  updateFormData('items', references);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          {/* LinkedIn URL */}
          <div className="mb-4">
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={reference.isLinkedIn || false}
                onChange={(e) => {
                  const references = [...formData.items];
                  references[index].isLinkedIn = e.target.checked;
                  updateFormData('items', references);
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs font-medium text-gray-700">LinkedIn Reference</span>
            </label>
            {reference.isLinkedIn && (
              <input
                type="url"
                value={reference.linkedInUrl}
                onChange={(e) => {
                  const references = [...formData.items];
                  references[index].linkedInUrl = e.target.value;
                  updateFormData('items', references);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="https://linkedin.com/in/john-smith"
              />
            )}
          </div>

          {/* Description/Comments */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Description/Comments
            </label>
            <textarea
              value={reference.description}
              onChange={(e) => {
                const references = [...formData.items];
                references[index].description = e.target.value;
                updateFormData('items', references);
              }}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Additional context about this reference..."
            />
          </div>

          {/* Reference Letter/Document Upload */}
          <div className="mb-4">
            <FileUpload
              label="Reference Letter/Document"
              currentFile={reference.documentUrl}
              onFileSelect={(file, error) => handleDocumentUpload(file, error, index)}
              onRemove={() => {
                const references = [...formData.items];
                references[index].documentUrl = '';
                references[index].documentFileName = '';
                updateFormData('items', references);
              }}
              accept=".pdf,.doc,.docx,.txt"
              maxSize={5}
              uploading={uploadStates[index]}
              error={uploadErrors[index]}
              showPreview={true}
            />
          </div>

          {/* Can Contact Checkbox */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={reference.canContact !== false}
                onChange={(e) => {
                  const references = [...formData.items];
                  references[index].canContact = e.target.checked;
                  updateFormData('items', references);
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs font-medium text-gray-700">
                Reference can be contacted
              </span>
            </label>
          </div>
        </div>
      ))}

      {/* Settings */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h5 className="font-medium text-gray-900 mb-3">Display Settings</h5>
        
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showContactInfo !== false}
              onChange={(e) => updateFormData('showContactInfo', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show contact information</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showDocuments === true}
              onChange={(e) => updateFormData('showDocuments', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show reference documents</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.enableContactForm === true}
              onChange={(e) => updateFormData('enableContactForm', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Enable contact reference form</span>
          </label>
        </div>
      </div>
    </div>
  );
};

const StoryFormFields = ({ formData, updateFormData, bookId }) => {
  const [uploadStates, setUploadStates] = useState({});

  const handleHeroMediaUpload = async (file, error) => {
    if (error) return;
    setUploadStates(prev => ({ ...prev, heroMedia: true }));
    
    try {
      const result = await profileService.uploadProfileMedia(file, bookId, 'story', 'hero');
      updateFormData('heroMedia', {
        url: result.url,
        type: file.type.startsWith('video/') ? 'video' : 'image',
        fileName: result.fileName
      });
    } catch (error) {
      console.error('Error uploading hero media:', error);
    } finally {
      setUploadStates(prev => ({ ...prev, heroMedia: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <FileUpload
          label="Hero Image/Video"
          currentFile={formData.heroMedia?.url}
          onFileSelect={handleHeroMediaUpload}
          onRemove={() => updateFormData('heroMedia', null)}
          accept="image/*,video/*"
          maxSize={50}
          uploading={uploadStates.heroMedia}
          showPreview={true}
        />
      </div>

      <div>
        <label className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            checked={formData.enableTabs || false}
            onChange={(e) => updateFormData('enableTabs', e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm font-medium text-gray-700">Enable Story Tabs</span>
        </label>

        {formData.enableTabs ? (
          <div className="space-y-4">
            {['overview', 'problem', 'solution', 'impact'].map((tabKey) => (
              <div key={tabKey}>
                <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                  {tabKey === 'overview' ? 'Overview' : 
                   tabKey === 'problem' ? 'Problem We Solve' :
                   tabKey === 'solution' ? 'Our Solution' : 'Impact & Results'}
                </label>
                <textarea
                  value={formData[tabKey] || ''}
                  onChange={(e) => updateFormData(tabKey, e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={`Tell your ${tabKey} story...`}
                />
              </div>
            ))}
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Story Content
            </label>
            <textarea
              value={formData.content || ''}
              onChange={(e) => updateFormData('content', e.target.value)}
              rows="8"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Tell your compelling product or service story..."
            />
          </div>
        )}
      </div>

      {/* Key Highlights */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Key Highlights
          </label>
          <button
            type="button"
            onClick={() => {
              const highlights = formData.highlights || [];
              highlights.push({
                title: '',
                description: '',
                metric: { value: '', label: '' }
              });
              updateFormData('highlights', highlights);
            }}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Highlight
          </button>
        </div>

        {(formData.highlights || []).map((highlight, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <input
                type="text"
                value={highlight.title}
                onChange={(e) => {
                  const highlights = [...(formData.highlights || [])];
                  highlights[idx].title = e.target.value;
                  updateFormData('highlights', highlights);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Highlight title"
              />
              <input
                type="text"
                value={highlight.metric?.value || ''}
                onChange={(e) => {
                  const highlights = [...(formData.highlights || [])];
                  highlights[idx].metric = { 
                    ...highlights[idx].metric, 
                    value: e.target.value 
                  };
                  updateFormData('highlights', highlights);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Metric (e.g., 95%, $10M)"
              />
            </div>
            <textarea
              value={highlight.description}
              onChange={(e) => {
                const highlights = [...(formData.highlights || [])];
                highlights[idx].description = e.target.value;
                updateFormData('highlights', highlights);
              }}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-3"
              placeholder="Highlight description"
            />
            <button
              type="button"
              onClick={() => {
                const highlights = (formData.highlights || []).filter((_, i) => i !== idx);
                updateFormData('highlights', highlights);
              }}
              className="text-red-600 hover:text-red-700 text-sm"
            >
              <Trash2 className="h-4 w-4 inline mr-1" />
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

<DemoFormFields/>

const SpecificationsFormFields = ({ formData, updateFormData, bookId }) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Product Specifications
        </label>
        <button
          type="button"
          onClick={() => {
            const specs = formData.specifications || [];
            specs.push({
              category: '',
              items: []
            });
            updateFormData('specifications', specs);
          }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Category
        </button>
      </div>

      {(formData.specifications || []).map((category, catIdx) => (
        <div key={catIdx} className="border border-gray-200 rounded-lg p-4">
          <input
            type="text"
            value={category.category}
            onChange={(e) => {
              const specs = [...(formData.specifications || [])];
              specs[catIdx].category = e.target.value;
              updateFormData('specifications', specs);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-3 font-medium"
            placeholder="Category name (e.g., Technical Specifications)"
          />
          
          <textarea
            value={category.items?.join('\n') || ''}
            onChange={(e) => {
              const specs = [...(formData.specifications || [])];
              specs[catIdx].items = e.target.value.split('\n').filter(item => item.trim());
              updateFormData('specifications', specs);
            }}
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Enter specifications (one per line, e.g., 'Weight: 2.5 kg')"
          />
          
          <button
            type="button"
            onClick={() => {
              const specs = (formData.specifications || []).filter((_, i) => i !== catIdx);
              updateFormData('specifications', specs);
            }}
            className="mt-3 text-red-600 hover:text-red-700 text-sm"
          >
            <Trash2 className="h-4 w-4 inline mr-1" />
            Remove Category
          </button>
        </div>
      ))}
    </div>
  );
};

const CredentialsFormFields = ({ formData, updateFormData, bookId }) => {
  const [uploadStates, setUploadStates] = useState({});

  const handleCredentialUpload = async (file, error, credIndex, field = 'certificateUrl') => {
    if (error) return;
    setUploadStates(prev => ({ ...prev, [`${credIndex}-${field}`]: true }));
    
    try {
      const result = await profileService.uploadProfileMedia(file, bookId, 'credentials', credIndex);
      const credentials = [...(formData.credentials || [])];
      credentials[credIndex][field] = result.url;
      updateFormData('credentials', credentials);
    } catch (error) {
      console.error('Error uploading credential:', error);
    } finally {
      setUploadStates(prev => ({ ...prev, [`${credIndex}-${field}`]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Credentials & Certifications
        </label>
        <button
          type="button"
          onClick={() => {
            const credentials = formData.credentials || [];
            credentials.push({
              id: Date.now(),
              name: '',
              issuer: '',
              description: '',
              issueDate: '',
              category: 'certifications',
              isVerified: false
            });
            updateFormData('credentials', credentials);
          }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Credential
        </button>
      </div>

      {(formData.credentials || []).map((credential, idx) => (
        <div key={idx} className="border border-gray-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              value={credential.name}
              onChange={(e) => {
                const credentials = [...(formData.credentials || [])];
                credentials[idx].name = e.target.value;
                updateFormData('credentials', credentials);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Credential name"
            />
            <input
              type="text"
              value={credential.issuer}
              onChange={(e) => {
                const credentials = [...(formData.credentials || [])];
                credentials[idx].issuer = e.target.value;
                updateFormData('credentials', credentials);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Issuing organization"
            />
          </div>

          <textarea
            value={credential.description}
            onChange={(e) => {
              const credentials = [...(formData.credentials || [])];
              credentials[idx].description = e.target.value;
              updateFormData('credentials', credentials);
            }}
            rows="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
            placeholder="Credential description"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <select
              value={credential.category}
              onChange={(e) => {
                const credentials = [...(formData.credentials || [])];
                credentials[idx].category = e.target.value;
                updateFormData('credentials', credentials);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="certifications">Certification</option>
              <option value="licenses">License</option>
              <option value="awards">Award</option>
              <option value="accreditations">Accreditation</option>
            </select>
            <input
              type="date"
              value={credential.issueDate}
              onChange={(e) => {
                const credentials = [...(formData.credentials || [])];
                credentials[idx].issueDate = e.target.value;
                updateFormData('credentials', credentials);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Issue date"
            />
          </div>

          <FileUpload
            label="Certificate/Document"
            currentFile={credential.certificateUrl}
            onFileSelect={(file, error) => handleCredentialUpload(file, error, idx, 'certificateUrl')}
            onRemove={() => {
              const credentials = [...(formData.credentials || [])];
              credentials[idx].certificateUrl = '';
              updateFormData('credentials', credentials);
            }}
            accept="image/*,.pdf"
            maxSize={10}
            uploading={uploadStates[`${idx}-certificateUrl`]}
            showPreview={true}
          />

          <button
            type="button"
            onClick={() => {
              const credentials = (formData.credentials || []).filter((_, i) => i !== idx);
              updateFormData('credentials', credentials);
            }}
            className="mt-3 text-red-600 hover:text-red-700 text-sm"
          >
            <Trash2 className="h-4 w-4 inline mr-1" />
            Remove
          </button>
        </div>
      ))}
    </div>
  );
};

const PricingFormFields = ({ formData, updateFormData, bookId }) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Pricing Tiers
        </label>
        <button
          type="button"
          onClick={() => {
            const tiers = formData.pricingTiers || [];
            tiers.push({
              name: '',
              price: '',
              period: 'monthly',
              features: [],
              highlighted: false,
              buttonText: 'Get Started',
              buttonUrl: ''
            });
            updateFormData('pricingTiers', tiers);
          }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Tier
        </button>
      </div>

      {(formData.pricingTiers || []).map((tier, idx) => (
        <div key={idx} className="border border-gray-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <input
              type="text"
              value={tier.name}
              onChange={(e) => {
                const tiers = [...(formData.pricingTiers || [])];
                tiers[idx].name = e.target.value;
                updateFormData('pricingTiers', tiers);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Tier name (e.g., Basic, Pro)"
            />
            <input
              type="text"
              value={tier.price}
              onChange={(e) => {
                const tiers = [...(formData.pricingTiers || [])];
                tiers[idx].price = e.target.value;
                updateFormData('pricingTiers', tiers);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Price (e.g., $29, Free)"
            />
            <select
              value={tier.period}
              onChange={(e) => {
                const tiers = [...(formData.pricingTiers || [])];
                tiers[idx].period = e.target.value;
                updateFormData('pricingTiers', tiers);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="one-time">One-time</option>
            </select>
          </div>

          <textarea
            value={tier.features?.join('\n') || ''}
            onChange={(e) => {
              const tiers = [...(formData.pricingTiers || [])];
              tiers[idx].features = e.target.value.split('\n').filter(f => f.trim());
              updateFormData('pricingTiers', tiers);
            }}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
            placeholder="Features included (one per line)"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              value={tier.buttonText}
              onChange={(e) => {
                const tiers = [...(formData.pricingTiers || [])];
                tiers[idx].buttonText = e.target.value;
                updateFormData('pricingTiers', tiers);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Button text"
            />
            <input
              type="url"
              value={tier.buttonUrl}
              onChange={(e) => {
                const tiers = [...(formData.pricingTiers || [])];
                tiers[idx].buttonUrl = e.target.value;
                updateFormData('pricingTiers', tiers);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Button URL"
            />
          </div>

          <div className="flex justify-between items-center">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={tier.highlighted || false}
                onChange={(e) => {
                  const tiers = [...(formData.pricingTiers || [])];
                  tiers[idx].highlighted = e.target.checked;
                  updateFormData('pricingTiers', tiers);
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Highlight this tier</span>
            </label>
            
            <button
              type="button"
              onClick={() => {
                const tiers = (formData.pricingTiers || []).filter((_, i) => i !== idx);
                updateFormData('pricingTiers', tiers);
              }}
              className="text-red-600 hover:text-red-700 text-sm"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const EngagementFormFields = ({ formData, updateFormData, bookId }) => {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Engagement Features
        </label>
        <div className="grid grid-cols-2 gap-4">
          {[
            { key: 'enableInterest', label: 'Expression of Interest' },
            { key: 'enableShares', label: 'Allow Sharing' },
            { key: 'enableBookmarks', label: 'Allow Bookmarking' },
            { key: 'showStats', label: 'Show Engagement Stats' }
          ].map((option) => (
            <label key={option.key} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData[option.key] || false}
                onChange={(e) => updateFormData(option.key, e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Call-to-Action Buttons
          </label>
          <button
            type="button"
            onClick={() => {
              const options = formData.purchaseOptions || [];
              options.push({
                label: '',
                url: '',
                price: '',
                type: 'purchase',
                primary: false,
                external: true
              });
              updateFormData('purchaseOptions', options);
            }}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Button
          </button>
        </div>

        {(formData.purchaseOptions || []).map((option, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <input
                type="text"
                value={option.label}
                onChange={(e) => {
                  const options = [...(formData.purchaseOptions || [])];
                  options[idx].label = e.target.value;
                  updateFormData('purchaseOptions', options);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Button label (e.g., Buy Now)"
              />
              <input
                type="url"
                value={option.url}
                onChange={(e) => {
                  const options = [...(formData.purchaseOptions || [])];
                  options[idx].url = e.target.value;
                  updateFormData('purchaseOptions', options);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="URL"
              />
              <input
                type="text"
                value={option.price}
                onChange={(e) => {
                  const options = [...(formData.purchaseOptions || [])];
                  options[idx].price = e.target.value;
                  updateFormData('purchaseOptions', options);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Price (optional)"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={option.primary || false}
                  onChange={(e) => {
                    const options = [...(formData.purchaseOptions || [])];
                    options[idx].primary = e.target.checked;
                    updateFormData('purchaseOptions', options);
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Primary Button</span>
              </label>
              
              <button
                type="button"
                onClick={() => {
                  const options = (formData.purchaseOptions || []).filter((_, i) => i !== idx);
                  updateFormData('purchaseOptions', options);
                }}
                className="text-red-600 hover:text-red-700 text-sm"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const GenericFormFields = ({ formData, updateFormData, bookId }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Content
    </label>
    <textarea
      value={formData.content || ''}
      onChange={(e) => updateFormData('content', e.target.value)}
      rows="6"
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      placeholder="Enter section content..."
    />
  </div>
);